import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { Sparkle } from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'
import FloorPlan from '@/components/landing/floor-plan'
import { DAY_STEPS } from '@/constants/landing'

const DayAtHome: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const stepRefs = useRef<Array<HTMLElement | null>>([])
    const step = DAY_STEPS[active]

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return

                const index = Number((entry.target as HTMLElement).dataset.index)

                if (!Number.isNaN(index)) setActive(index)
            })
        }, { rootMargin: '-48% 0px -48% 0px' })

        stepRefs.current.forEach(node => {
            if (node) observer.observe(node)
        })

        return () => observer.disconnect()
    }, [])

    return (
        <section id='everyday' aria-labelledby='everyday-title' className='relative bg-ink text-ink-foreground'>
            <div className='mx-auto max-w-[1440px] px-5 pt-20 md:px-8 md:pt-28'>
                <div className='grid gap-6 lg:grid-cols-12 lg:gap-10'>
                    <Reveal className='lg:col-span-7'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                            A day at home
                        </p>

                        <h2 id='everyday-title' className='mt-5 font-heading text-[clamp(2.6rem,5.4vw,5rem)] leading-[0.96] tracking-[-0.02em]'>
                            Four rooms, one <em className='italic text-ember'>unhurried</em> day.
                        </h2>
                    </Reveal>

                    <Reveal delay={150} className='flex items-end lg:col-span-5'>
                        <p className='max-w-md text-lg leading-relaxed text-ink-foreground/70'>
                            Oriel works around your routine, not the other way round. Scroll to follow it from the first light to the last lamp.
                        </p>
                    </Reveal>
                </div>
            </div>

            <div className='mx-auto max-w-[1440px] px-5 pb-20 pt-12 md:px-8 md:pb-28 lg:grid lg:grid-cols-12 lg:gap-10'>
                <div className='hidden lg:col-span-7 lg:block'>
                    <div className='sticky top-24 h-[calc(100svh-8rem)] min-h-[32rem] overflow-hidden rounded-[2rem] bg-white/5'>
                        <div className='absolute inset-0 isolate'>
                            {DAY_STEPS.map((item, index) => (
                                <div
                                    key={item.id}
                                    aria-hidden={index !== active}
                                    className={cn(
                                        'absolute inset-0 transition-[opacity,scale] ease-[cubic-bezier(0.4,0,0.2,1)]',
                                        index === active ? 'z-[1] scale-100 opacity-100 duration-[1200ms]' : 'z-0 scale-[1.04] opacity-0 delay-[1200ms] duration-0'
                                    )}
                                >
                                    <Image
                                        src={item.src}
                                        alt={item.alt}
                                        fill
                                        sizes='(min-width: 1440px) 820px, 58vw'
                                        className='object-cover'
                                    />
                                </div>
                            ))}
                        </div>

                        <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,oklch(0.165_0.01_55/0.55)_0%,transparent_28%,transparent_62%,oklch(0.165_0.01_55/0.75)_100%)]' />

                        <div className='absolute left-5 top-5 flex items-center gap-3 rounded-full border border-white/15 bg-ink/50 py-2 pl-3 pr-4 backdrop-blur-md'>
                            <span className='font-mono text-sm text-ember tabular-nums'>
                                {step.time}
                            </span>

                            <span className='h-3 w-px bg-white/25' />

                            <span className='text-sm text-ink-foreground/90'>
                                {step.room}
                            </span>
                        </div>

                        <div className='absolute bottom-5 right-5 w-[min(19rem,42%)] rounded-2xl border border-white/12 bg-ink/60 p-3 backdrop-blur-xl'>
                            <div className='flex items-center justify-between px-1 pb-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-foreground/55'>
                                <span>
                                    Home map
                                </span>

                                <span className='text-ember'>
                                    Live
                                </span>
                            </div>

                            <FloorPlan stop={step.stop} room={step.room} className='w-full' />
                        </div>

                        <div className='absolute bottom-6 left-6 flex gap-1.5' aria-hidden='true'>
                            {DAY_STEPS.map((item, index) => (
                                <span
                                    key={item.id}
                                    className={cn(
                                        'h-1 rounded-full transition-[width,background-color] duration-500',
                                        index === active ? 'w-8 bg-ember' : 'w-3 bg-white/35'
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <ol className='flex flex-col gap-14 lg:col-span-5 lg:gap-0'>
                    {DAY_STEPS.map((item, index) => (
                        <li
                            key={item.id}
                            ref={node => {
                                stepRefs.current[index] = node
                            }}
                            data-index={index}
                            className='lg:flex lg:min-h-[78svh] lg:items-center'
                        >
                            <article
                                className={cn(
                                    'transition-opacity duration-700',
                                    index === active ? 'lg:opacity-100' : 'lg:opacity-35'
                                )}
                            >
                                <div className='relative mb-6 aspect-[4/3] overflow-hidden rounded-3xl lg:hidden'>
                                    <Image
                                        src={item.src}
                                        alt={item.alt}
                                        fill
                                        sizes='(min-width: 768px) 90vw, 100vw'
                                        className='object-cover'
                                    />

                                    <span className='absolute left-4 top-4 rounded-full border border-white/15 bg-ink/55 px-3 py-1.5 font-mono text-xs text-ember backdrop-blur-md'>
                                        {item.time}
                                    </span>
                                </div>

                                <p className='flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-foreground/55'>
                                    <span className='text-ember tabular-nums'>
                                        {item.time}
                                    </span>

                                    <span className='h-px w-6 bg-white/25' />

                                    {item.room}
                                </p>

                                <h3 className='mt-4 font-heading text-[clamp(2.25rem,3.6vw,3.5rem)] leading-[1] tracking-[-0.015em]'>
                                    {item.title}
                                </h3>

                                <p className='mt-4 max-w-md text-lg leading-relaxed text-ink-foreground/75'>
                                    {item.body}
                                </p>

                                <p className='mt-6 flex max-w-md items-start gap-3 border-t border-white/10 pt-5 text-[15px] leading-relaxed text-ink-foreground/65'>
                                    <Sparkle className='mt-1 size-4 shrink-0 text-ember' />
                                    {item.note}
                                </p>
                            </article>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}

export default DayAtHome
